"use client";

import { useState } from "react";
import { LIBRARY_TYPE_KEYS, LIBRARY_TYPE_LABEL } from "@/lib/libraryFormatters";

/**
 * BUG_EVIDENCE_PACKET_2026-05-25 B-LIB-01 — Library needs a direct
 * "Add reference / thought" entry point. This modal is the
 * companion to LibraryEditModal but for *new* items: it has no
 * baseline `detail` to diff against, just a clean form that submits
 * the full payload via `addToContentQueue`.
 *
 * Defaults to content_type="note" which is the closest match to the
 * user-requested "thought" semantics until the backend type enum
 * grows. The user can override via the Type dropdown.
 */

export interface LibraryCreatePayload {
  title: string;
  description?: string;
  url?: string;
  content_type: string;
  duration_min?: number;
}

interface Props {
  isBusy: boolean;
  onCancel: () => void;
  onSubmit: (payload: LibraryCreatePayload) => void;
}

export default function LibraryCreateModal({ isBusy, onCancel, onSubmit }: Props) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [url, setUrl] = useState("");
  const [contentType, setContentType] = useState<string>("note");
  const [durationMin, setDurationMin] = useState<number | "">("");
  const [error, setError] = useState<string | null>(null);

  function handleSubmit() {
    const trimmedTitle = title.trim();
    if (!trimmedTitle) {
      setError("Title cannot be empty.");
      return;
    }
    const numericDuration = durationMin === "" ? undefined : Number(durationMin);
    if (numericDuration !== undefined && (numericDuration < 1 || numericDuration > 480)) {
      setError("Duration must be between 1 and 480 minutes.");
      return;
    }
    setError(null);
    const payload: LibraryCreatePayload = {
      title: trimmedTitle,
      content_type: contentType || "note",
    };
    const trimmedDescription = description.trim();
    if (trimmedDescription) payload.description = trimmedDescription;
    const trimmedUrl = url.trim();
    if (trimmedUrl) payload.url = trimmedUrl;
    if (numericDuration !== undefined) payload.duration_min = numericDuration;
    onSubmit(payload);
  }

  return (
    <div
      data-testid="library-create-modal"
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/25 backdrop-blur-[2px] px-4"
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-labelledby="library-create-title"
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="h-2 rounded-t-2xl bg-forest-med" />
        <div className="p-5">
          <div className="flex items-start justify-between gap-3">
            <span className="rounded-full bg-sage-pale px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-forest-med">
              Add reference
            </span>
            <button
              type="button"
              onClick={onCancel}
              className="flex h-7 w-7 items-center justify-center rounded-full text-muted hover:bg-secondary hover:text-text"
              aria-label="Close"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <h2 id="library-create-title" className="sr-only">
            Add a reference or thought to your library
          </h2>

          <div className="mt-4 space-y-3">
            <Field label="Title">
              <input
                type="text"
                data-testid="library-create-title-input"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="What is it called?"
                maxLength={300}
                className="w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/40 focus:outline-none"
                autoFocus
              />
            </Field>

            <Field label="Description / notes / thought">
              <textarea
                data-testid="library-create-description-input"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                maxLength={8000}
                rows={4}
                placeholder="Capture the gist, your notes, or the thought you want to come back to…"
                className="w-full resize-y rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/40 focus:outline-none"
              />
            </Field>

            <div className="grid grid-cols-2 gap-3">
              <Field label="Type">
                <select
                  data-testid="library-create-type-input"
                  value={contentType}
                  onChange={(e) => setContentType(e.target.value)}
                  className="w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/40 focus:outline-none"
                >
                  {LIBRARY_TYPE_KEYS.map((typeKey) => (
                    <option key={typeKey} value={typeKey}>
                      {LIBRARY_TYPE_LABEL[typeKey]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label="Duration (min, optional)">
                <input
                  type="number"
                  data-testid="library-create-duration-input"
                  value={durationMin}
                  onChange={(e) =>
                    setDurationMin(e.target.value === "" ? "" : Math.max(1, Number(e.target.value) || 0))
                  }
                  min={1}
                  max={480}
                  placeholder=""
                  className="w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/40 focus:outline-none"
                />
              </Field>
            </div>

            <Field label="URL (optional)">
              <input
                type="url"
                data-testid="library-create-url-input"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://…"
                maxLength={2000}
                className="w-full rounded-xl border border-border bg-bg px-3 py-2 text-sm text-text focus:border-forest-med/40 focus:outline-none"
              />
            </Field>
          </div>

          {error && (
            <p className="mt-3 rounded-xl bg-amber/10 px-3 py-2 text-xs text-amber">{error}</p>
          )}

          <div className="mt-5 flex gap-2">
            <button
              type="button"
              data-testid="library-create-save"
              onClick={handleSubmit}
              disabled={isBusy}
              className="flex-1 rounded-full bg-forest-med px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-forest disabled:opacity-60"
            >
              {isBusy ? "Adding…" : "Add to Library"}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={isBusy}
              className="rounded-full border border-border bg-white px-4 py-2.5 text-sm font-medium text-text transition-colors hover:border-forest-med/40 hover:text-forest-med disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted">{label}</span>
      {children}
    </label>
  );
}
